import { useState, useEffect } from 'react'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { baseURL } from '../../config'
import { getAuthHeaders } from '../../services/authService'
import TeamHeader from './Team/TeamHeader'
import SharedDevicePoolCard from './Team/SharedDevicePoolCard'
import MembersTable from './Team/MembersTable'
import SubscriptionWarningBanner from './Team/SubscriptionWarningBanner'
import CreateTeamModal from './Team/CreateTeamModal'
import InviteUserModal from './Team/InviteUserModal'
import UpgradeModal from './Team/UpgradeModal'
import LeaveTeamConfirmModal from './Team/LeaveTeamConfirmModal'
import { NoTeamPlanView, CreateTeamView } from './Team/NoTeamView'
import { alertIfStoreBilled } from '../../utils/storeBilledCheckout'

function TeamSettings({ isDarkMode, activeTab }) {
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showInviteModal, setShowInviteModal] = useState(false)
	const [showUpgradeModal, setShowUpgradeModal] = useState(false)
	const [teamName, setTeamName] = useState('')
	const [inviteEmail, setInviteEmail] = useState('')
	const [creating, setCreating] = useState(false)
	const [inviting, setInviting] = useState(false)
	const [loading, setLoading] = useState(true)
	const [teamData, setTeamData] = useState(null)
	const [redirecting, setRedirecting] = useState(false)
	const [hasTeamPlan, setHasTeamPlan] = useState(false)
	const [refreshTrigger, setRefreshTrigger] = useState(0)
	const [teamMembership, setTeamMembership] = useState(null)
	const [showLeaveTeamConfirm, setShowLeaveTeamConfirm] = useState(false)
	const [editingName, setEditingName] = useState(false)
	const [newTeamName, setNewTeamName] = useState('')
	const [updatingName, setUpdatingName] = useState(false)
	const [togglingSharedPool, setTogglingSharedPool] = useState(false)

	const fetchTeamData = () => {
		setRefreshTrigger(prev => prev + 1)
	}

	useEffect(() => {
		const controller = new AbortController()

		if (activeTab === 'team') {
			const loadData = async () => {
				try {
					setLoading(true)

					const response = await axios.get(`${baseURL}/api/team/my-team`, {
						signal: controller.signal,
					})

					setTeamData(response.data)
					setHasTeamPlan(response.data?.hasTeamSubscription || false)
				} catch (error) {
					if (error.name !== 'CanceledError') {
						console.error('Error fetching team:', error)
					}
				} finally {
					if (!controller.signal.aborted) {
						setLoading(false)
					}
				}
			}

			loadData()
		}

		const checkTeamMembership = async () => {
			try {
				const response = await fetch(`${baseURL}/api/team/check-membership`, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: localStorage.getItem('jwtToken'),
					},
				})

				if (response.ok) {
					const data = await response.json()
					setTeamMembership(data)
				}
			} catch (error) {
				console.error('Error checking team membership:', error)
			}
		}

		checkTeamMembership()

		return () => {
			controller.abort()
		}
	}, [activeTab, refreshTrigger])

	const handleCreateTeam = async e => {
		e.preventDefault()
		if (!teamName.trim()) return

		setCreating(true)
		try {
			const response = await axios.post(`${baseURL}/api/team/create`, { name: teamName })
			if (response.data.success) {
				setShowCreateModal(false)
				setTeamName('')
				await fetchTeamData()
			}
		} catch (error) {
			console.error('Error creating team:', error)
			alert(error.response?.data?.message || 'Failed to create team')
		} finally {
			setCreating(false)
		}
	}

	const handleInviteUser = async e => {
		e.preventDefault()
		if (!inviteEmail.trim()) return

		setInviting(true)
		try {
			const response = await axios.post(`${baseURL}/api/team/invite`, { email: inviteEmail })
			if (response.data.success) {
				setShowInviteModal(false)
				setInviteEmail('')
				fetchTeamData()
			}
		} catch (error) {
			console.error('Error inviting user:', error)
			alert(error.response?.data?.message || 'Failed to invite user')
		} finally {
			setInviting(false)
		}
	}

	const handleRemoveMember = async memberId => {
		if (!window.confirm('Are you sure you want to remove this member?')) return

		try {
			const response = await axios.delete(`${baseURL}/api/team/member/${memberId}`)
			if (response.data.success) fetchTeamData()
		} catch (error) {
			console.error('Error removing member:', error)
			alert(error.response?.data?.message || 'Failed to remove member')
		}
	}

	const handleLeaveTeam = async () => {
		if (!window.confirm('Are you sure you want to leave this team? You will lose access to team benefits.')) return

		try {
			const response = await axios.post(`${baseURL}/api/team/leave`)
			if (response.data.success) window.location.reload()
		} catch (error) {
			console.error('Error leaving team:', error)
			alert(error.response?.data?.message || 'Failed to leave team')
		}
	}

	const handleUpdateTeamName = async () => {
		if (!newTeamName.trim()) {
			setEditingName(false)
			return
		}

		setUpdatingName(true)
		try {
			const response = await axios.patch(`${baseURL}/api/team/update-name`, { name: newTeamName })
			if (response.data.success) {
				setEditingName(false)
				setNewTeamName('')
				fetchTeamData()
			}
		} catch (error) {
			console.error('Error updating team name:', error)
			alert(error.response?.data?.message || 'Failed to update team name')
		} finally {
			setUpdatingName(false)
		}
	}

	const handleToggleSharedPool = async () => {
		setTogglingSharedPool(true)
		try {
			const response = await axios.patch(`${baseURL}/api/team/toggle-shared-pool`)
			if (response.data.success) fetchTeamData()
		} catch (error) {
			console.error('Error toggling shared pool:', error)
			alert(error.response?.data?.message || 'Failed to toggle shared device pool')
		} finally {
			setTogglingSharedPool(false)
		}
	}

	const handleUpgrade = async () => {
		setRedirecting(true)
		try {
			if (teamData?.ownerHasActiveSubscription) {
				const returnUrl = encodeURIComponent(window.location.href)
				const response = await fetch(
					`${baseURL}/api/stripe/customer-portal?returnUrl=${returnUrl}`,
					{ method: 'GET', headers: getAuthHeaders() }
				)
				if (!response.ok) throw new Error('Failed to get customer portal URL')
				const portalUrl = await response.text()
				window.location.href = portalUrl
			} else {
				handleUpgradeToTeam()
			}
		} catch (error) {
			console.error('Error redirecting:', error)
			alert('Failed to redirect')
			setRedirecting(false)
		}
	}

	const handleUpgradeToTeam = async (skipTeamCheck = false) => {
		if (!skipTeamCheck && teamMembership?.isMember && !teamMembership?.isOwner) {
			setShowLeaveTeamConfirm(true)
			return
		}

		setRedirecting(true)
		try {
			const jwt = localStorage.getItem('jwtToken')
			const user = jwtDecode(jwt)

			const response = await fetch(`${baseURL}/api/stripe/create-checkout-session`, {
				method: 'POST',
				headers: getAuthHeaders(),
				body: JSON.stringify({
					planType: 'monthly',
					planName: 'team',
					customerEmail: user.email,
					cancelUrl: `${window.location.origin}/settings/team`,
					seats: 2,
				}),
			})

			if (await alertIfStoreBilled(response)) {
				setRedirecting(false)
				return
			}

			if (!response.ok) throw new Error('Failed to create checkout session')
			const session = await response.json()
			window.location.href = session?.url
		} catch (error) {
			console.error('Error upgrading to team:', error)
			alert('Failed to upgrade. Please try again.')
			setRedirecting(false)
		}
	}

	const handleLeaveTeamForUpgrade = async () => {
		try {
			const response = await fetch(`${baseURL}/api/team/leave-for-upgrade`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: localStorage.getItem('jwtToken'),
				},
			})

			if (!response.ok) {
				const errorData = await response.json()
				throw new Error(errorData.message || 'Failed to leave team')
			}

			setShowLeaveTeamConfirm(false)
			setTeamMembership({ isMember: false, isOwner: false })
			handleUpgradeToTeam(true)
		} catch (error) {
			console.error('Error leaving team:', error)
			alert('Failed to leave team: ' + error.message)
			setShowLeaveTeamConfirm(false)
		}
	}

	const handleInviteClick = () => {
		if (teamData.team.memberCount >= teamData.team.seats) {
			setShowUpgradeModal(true)
		} else {
			setShowInviteModal(true)
		}
	}

	if (loading) {
		return (
			<div className="flex justify-center items-center py-32">
				<div
					className={`animate-spin rounded-full h-12 w-12 border-4 border-t-transparent ${
						isDarkMode ? 'border-gray-400' : 'border-gray-500'
					}`}
				/>
			</div>
		)
	}

	return (
		<>
			{teamData?.hasTeam ? (
				<div className="space-y-6">
					{!teamData.ownerSubscriptionActive && (
						<SubscriptionWarningBanner
							isDarkMode={isDarkMode}
							redirecting={redirecting}
							onManageSubscription={handleUpgrade}
							isOwner={teamData.isOwner}
						/>
					)}

					<TeamHeader
						isDarkMode={isDarkMode}
						teamData={teamData}
						editingName={editingName}
						setEditingName={setEditingName}
						newTeamName={newTeamName}
						setNewTeamName={setNewTeamName}
						updatingName={updatingName}
						onUpdateTeamName={handleUpdateTeamName}
						onLeaveTeam={handleLeaveTeam}
						onInviteClick={handleInviteClick}
					/>

					<SharedDevicePoolCard
						isDarkMode={isDarkMode}
						teamData={teamData}
						togglingSharedPool={togglingSharedPool}
						onToggleSharedPool={handleToggleSharedPool}
					/>

					<MembersTable
						isDarkMode={isDarkMode}
						teamData={teamData}
						onRemoveMember={handleRemoveMember}
					/>
				</div>
			) : !hasTeamPlan ? (
				<NoTeamPlanView
					isDarkMode={isDarkMode}
					redirecting={redirecting}
					onUpgrade={() => handleUpgradeToTeam()}
				/>
			) : !teamData?.hasTeam ? (
				<CreateTeamView
					isDarkMode={isDarkMode}
					onCreateClick={() => setShowCreateModal(true)}
				/>
			) : null}

			{showCreateModal && (
				<CreateTeamModal
					isDarkMode={isDarkMode}
					teamName={teamName}
					setTeamName={setTeamName}
					creating={creating}
					onSubmit={handleCreateTeam}
					onClose={() => {
						setShowCreateModal(false)
						setTeamName('')
					}}
				/>
			)}

			{showInviteModal && (
				<InviteUserModal
					isDarkMode={isDarkMode}
					inviteEmail={inviteEmail}
					setInviteEmail={setInviteEmail}
					inviting={inviting}
					onSubmit={handleInviteUser}
					onClose={() => {
						setShowInviteModal(false)
						setInviteEmail('')
					}}
				/>
			)}

			{showUpgradeModal && (
				<UpgradeModal
					isDarkMode={isDarkMode}
					seats={teamData?.team?.seats}
					redirecting={redirecting}
					onUpgrade={handleUpgrade}
					onClose={() => setShowUpgradeModal(false)}
				/>
			)}

			{showLeaveTeamConfirm && (
				<LeaveTeamConfirmModal
					isDarkMode={isDarkMode}
					teamName={teamMembership?.teamName}
					onLeaveAndUpgrade={handleLeaveTeamForUpgrade}
					onClose={() => setShowLeaveTeamConfirm(false)}
				/>
			)}
		</>
	)
}

export default TeamSettings
